import { Address } from '../../common/model/address';
import { ModelBase } from '../../core/model/model-base';
import { Image } from '../../core/model/image';
import { LocalDate } from '../../core/model/local-date';
import { DebtCollectionConfiguration } from './debt-collection-configuration';
import { LanguageService } from '../../core/service/language.service';
import { Currencies } from '../../core/pipe/currency.pipe';
import { SelectionItem } from '../../core/component/select/select.component';
import { CompanyBankAccount } from './company-bank-account';
import { CompanyCustomProperty } from './company-custom-property';
import { CompanyDocument } from './company-document';
import { CompanyCostBearer, CompanyCostPlace } from './company-cost-place';
import { PostTargetModel } from '../service/base/post-target-loader.interface';
import { AccountPlanDimensions } from '../../accounting/model/account-plan-dimensions';
import { DimensionType } from '../../accounting/model/const/enum';
import { Money } from '../../core/model/money';
import { DebtCollectionSupplierKind, DebtCollectionSupplierKindToString } from './const/enums';

export enum CompanyForm {
  SE_Aktiebolag = 100,
  SE_EnskildFirma = 101,
  SE_BostadsrattsForening = 102,
  SE_OaktaBostadsrattsForening = 103,
  SE_EkonomiskForening = 104,
  SE_Kommanditbolag = 105,
  SE_Handelsbolag = 106,
  SE_Stiftelse = 107,
  SE_RegistreratTrossamfund = 108,
  DK_Aktieselskab = 200,
  DK_Enkeltmandsvirksomhed = 201,
  DK_Andelsboligforening = 202,
  DK_PersonligMindreVirksomhed = 203,
  DK_Interessentskab = 204,
  DK_Anpartsselskab = 205,
  FI_Osakeyhtio = 300,
  FI_YksittainenElinkeinonharjoittaja = 301,
  FI_Osuuskunta = 304,
}

export enum AccountingExportDateLimit {
  // All unsent accounting, regardless of accounting date will be sent.
  NoLimit = 0,

  // Accounting with accounting date earlier than the first of the current month will be sent.
  LastMonthBreak = 1,

  // Accounting with accounting date less than or equal to todays date will be sent.
  Today = 2,
}

export enum AccountingExportMethodKind {
  None = 0,
  HogiaOb = 1,
  HogiaHek = 2,
  VismaControlEdge = 3,
  AgressoGl07Ftp = 4,
  SIE4Ftp = 5,
  Visma = 6,
  SIE4Email = 7,
  Fortnox = 8,
  RaindanceFtp = 9,
  VismaRorFtp = 10,
}

export enum UseEarlyTerminationKind {
  NotUsed = 0,
  UsedWithDefaultValueYes = 1,
  UsedWithDefaultValueNo = 2,
}

export enum CompanyNameplateType {
  Auto = 0,
  Oneline = 1,
  OnelineShort = 2,
  MultilineShort = 3,
}

export class CompanyForms {
  static getDefaultCompanyForm(): CompanyForm {
    switch (LanguageService.getCultureLang()) {
      case 'da-DK':
        CompanyForm.DK_Aktieselskab;
      case 'fi-FI':
        CompanyForm.FI_Osakeyhtio;
      default:
        return CompanyForm.SE_Aktiebolag;
    }
  }

  static getCompanyFormSelection(cultureCode: string): SelectionItem[] {
    switch (cultureCode) {
      case 'da-DK':
        return [
          new SelectionItem('Aktieselskab', CompanyForm.DK_Aktieselskab),
          new SelectionItem('Enkeltmandsvirksomhed', CompanyForm.DK_Enkeltmandsvirksomhed),
          new SelectionItem('Andelsboligforening', CompanyForm.DK_Andelsboligforening),
          new SelectionItem('Personlig mindre virksomhed', CompanyForm.DK_PersonligMindreVirksomhed),
          new SelectionItem('Interessentskab', CompanyForm.DK_Interessentskab),
          new SelectionItem('Anpartsselskab', CompanyForm.DK_Anpartsselskab),
        ];
      case 'fi-FI':
        return [
          new SelectionItem('Osakeyhtiö', CompanyForm.FI_Osakeyhtio),
          new SelectionItem('Yksittäinen elinkeinonharjoittaja', CompanyForm.FI_YksittainenElinkeinonharjoittaja),
          new SelectionItem('Osuuskunta', CompanyForm.FI_Osuuskunta),
        ];
      default:
        return [
          new SelectionItem('Aktiebolag', CompanyForm.SE_Aktiebolag),
          new SelectionItem('Bostadsrättsförening', CompanyForm.SE_BostadsrattsForening),
          new SelectionItem('Oäkta bostadsrättsförening', CompanyForm.SE_OaktaBostadsrattsForening),
          new SelectionItem('Enskild firma', CompanyForm.SE_EnskildFirma),
          new SelectionItem('Ekonomisk förening', CompanyForm.SE_EkonomiskForening),
          new SelectionItem('Kommanditbolag', CompanyForm.SE_Kommanditbolag),
          new SelectionItem('Handelsbolag', CompanyForm.SE_Handelsbolag),
          new SelectionItem('Stiftelse', CompanyForm.SE_Stiftelse),
          new SelectionItem('Registrerat trossamfund', CompanyForm.SE_RegistreratTrossamfund),
        ];
    }
  }
}

export class EmailAccountingConfiguration {
  constructor() {
    this.fromAccountingDate = LocalDate.fromDate(LocalDate.today().firstDayInMonth());
    this.dayOfMonth = 0;
  }
  enabled: boolean;
  fromAccountingDate: LocalDate;
  emailAddress: string;
  dayOfMonth: number;
}

export class Sie4ExportConfiguration {
  constructor() {
    this.dim1 = DimensionType.CostPlace;
    this.dim2 = DimensionType.CostBearer;
    this.dim3 = DimensionType.Undefined;
    this.dim4 = DimensionType.Undefined;
    this.dim5 = DimensionType.Undefined;
    this.dim6 = DimensionType.Project;
    this.dim7 = DimensionType.Undefined;
    this.dim8 = DimensionType.CounterPart;
    this.replaceLocalCharacters = true;
    this.encodingName = 'CP-437';
  }

  dim1: DimensionType;
  dim2: DimensionType;
  dim3: DimensionType;
  dim4: DimensionType;
  dim5: DimensionType;
  dim6: DimensionType;
  dim7: DimensionType;
  dim8: DimensionType;

  includeVerificationNumber: boolean;
  includeClosingBalance: boolean;
  includeOrganizationNo: boolean;
  includeCompanyCode: boolean;
  includeAccountingYearDescription: boolean;
  replaceLocalCharacters: boolean;
  encodingName: string;
}

export class Gl07ExportConfiguration {
  constructor() {
    this.dim1 = DimensionType.CostPlace;
    this.dim2 = DimensionType.CostBearer;
    this.dim3 = DimensionType.Undefined;
    this.dim4 = DimensionType.Undefined;
    this.dim5 = DimensionType.Undefined;
    this.dim6 = DimensionType.Project;
    this.dim7 = DimensionType.CounterPart;
    this.fileExtension = 'txt';
    this.interface = 'BI';
    this.voucherType = 'IN';
    this.client = '';
    this.bundleFile = false;
    this.asymmetricBundling = false;
    this.zeroFillAmounts = false;
    this.encodingName = 'UTF-8';
  }

  dim1: DimensionType;
  dim2: DimensionType;
  dim3: DimensionType;
  dim4: DimensionType;
  dim5: DimensionType;
  dim6: DimensionType;
  dim7: DimensionType;
  customerOrSupplierIdDim: DimensionType;
  d1Fixed: string;
  d2Fixed: string;
  d3Fixed: string;
  d4Fixed: string;
  d5Fixed: string;
  d6Fixed: string;
  d7Fixed: string;
  ledgerType: string;
  taxCode: string;
  customerOrSupplierIdDimFixed: string;
  fileExtension: string;
  bundleFile: boolean;
  asymmetricBundling: boolean;
  includeTodaysWork: boolean;
  zeroFillAmounts: boolean;
  interface: string;
  encodingName: string;
  voucherType: string;
  client: string;
  invoiceReference: InvoiceReferenceKind;
}

export class RaindanceExportConfiguration {
  constructor() {
    this.fileExtension = 'txt';
    this.bundleFile = false;
    this.asymmetricBundling = false;
    this.encodingName = 'CP-819';
    this.ansv = DimensionType.Undefined;
    this.vht = DimensionType.Undefined;
    this.akt = DimensionType.Undefined;
    this.obj = DimensionType.Undefined;
    this.proj = DimensionType.Project;
    this.mk = DimensionType.Undefined;
    this.fri = DimensionType.Undefined;
    this.motp = DimensionType.CounterPart;
    this.periodisering = DimensionType.Undefined;
  }

  fileExtension: string;
  enabled: boolean;
  bundleFile: boolean;
  asymmetricBundling: boolean;
  includeTodaysWork: boolean;
  encodingName: string;
  ansv: DimensionType;
  vht: DimensionType;
  akt: DimensionType;
  obj: DimensionType;
  proj: DimensionType;
  mk: DimensionType;
  fri: DimensionType;
  motp: DimensionType;
  periodisering: DimensionType;
  ansvFixed: string;
  vhtFixed: string;
  aktFixed: string;
  objFixed: string;
  projFixed: string;
  mkFixed: string;
  friFixed: string;
  motpFixed: string;
  periodiseringFixed: string;
}

export enum VismaRorAccountPartSourceKind {
  Undefined = 0,
  MainAccount = 1,
  CostPlace = 2,
  CostBearer = 3,
  Project = 4,
  CounterPart = 5,
  Function = 6,
  FixedValue = 99,
}

export enum VismaRorAccountPartCharacterKind {
  Numeric = 0,
  AlphaNumeric = 1,
}

export class VismaRorAccountPartConfiguration {
  constructor(
    public source: VismaRorAccountPartSourceKind = VismaRorAccountPartSourceKind.Undefined,
    public declaredLength: number = 10,
    public characterType: VismaRorAccountPartCharacterKind = VismaRorAccountPartCharacterKind.AlphaNumeric,
    public fixedValue: string = ''
  ) {}
}

export class VismaRorExportConfiguration {
  constructor() {
    this.externalCompany = '';
    this.internalCompany = '';
    this.senderRoutine = 'FAS';
    this.senderSubroutine = '';
    this.originType = '250';
    this.processingType = '250';
    this.fileExtension = 'TXT';
    this.includeTodaysWork = false;
    this.asymmetricBundling = true;
    this.konto1 = new VismaRorAccountPartConfiguration(VismaRorAccountPartSourceKind.CostPlace, 8, VismaRorAccountPartCharacterKind.Numeric);
    this.konto2 = new VismaRorAccountPartConfiguration(VismaRorAccountPartSourceKind.MainAccount, 5, VismaRorAccountPartCharacterKind.Numeric);
    this.konto3 = new VismaRorAccountPartConfiguration(VismaRorAccountPartSourceKind.CostBearer, 5, VismaRorAccountPartCharacterKind.Numeric);
    this.konto4 = new VismaRorAccountPartConfiguration();
    this.konto5 = new VismaRorAccountPartConfiguration();
    this.konto6 = new VismaRorAccountPartConfiguration(VismaRorAccountPartSourceKind.Project, 5, VismaRorAccountPartCharacterKind.Numeric);
    this.konto7 = new VismaRorAccountPartConfiguration();
    this.konto8 = new VismaRorAccountPartConfiguration();
    this.konto9 = new VismaRorAccountPartConfiguration();
    this.konto10 = new VismaRorAccountPartConfiguration();
  }

  enabled: boolean;
  externalCompany: string;
  internalCompany: string;
  senderRoutine: string;
  senderSubroutine: string;
  originType: string;
  processingType: string;
  fileExtension: string;
  includeTodaysWork: boolean;
  asymmetricBundling: boolean;
  konto1: VismaRorAccountPartConfiguration;
  konto2: VismaRorAccountPartConfiguration;
  konto3: VismaRorAccountPartConfiguration;
  konto4: VismaRorAccountPartConfiguration;
  konto5: VismaRorAccountPartConfiguration;
  konto6: VismaRorAccountPartConfiguration;
  konto7: VismaRorAccountPartConfiguration;
  konto8: VismaRorAccountPartConfiguration;
  konto9: VismaRorAccountPartConfiguration;
  konto10: VismaRorAccountPartConfiguration;
}

export class HogiaObExportConfiguration {
  constructor() {
    this.bundleVouchers = false;
    this.asymmetricBundling = false;
  }

  bundleVouchers: boolean;
  asymmetricBundling: boolean;
}

export enum InvoiceReferenceKind {
  None = 0,
  InvoiceNumber = 1,
  Ocr = 2,
}

export enum AccountingFileFormat {
  Sie4 = 0,
  Gl07 = 1,
  Raindance = 2,
  VismaRor = 3,
}

export const defaultAmortizationPlanLetterText = `Denna avbetalningsplan omfattar endast de fakturor och belopp som anges i dokumentet.
Betalning ska ske senast på respektive forfallodatum och med angiven betalningsreferens.
Om en delbetalning inte betalas i ratt tid har fordringsagaren ratt att saga upp avbetalningsplanen och krava betalning enligt vid var tid gallande regler och villkor.
Om ranta, avgifter eller andra tillagg ska utga under planens lopttid ska detta framga uttryckligen i dokumentet.
Digital signering av detta dokument utgor parternas godkannande av avbetalningsplanens innehall.`;

export class Company extends ModelBase {
  constructor() {
    super();
    this.accountPlanId = null;
    this.address = new Address();
    this.debtCollectionConfiguration = new DebtCollectionConfiguration();
    this.nextInvoiceNumber = 50000;
    this.allowChangeNextInvoiceNumber = this.isNew;
    this.allowChangeCurrency = this.isNew;
    this.firstAccountingDateToExport = null;
    this.ftpFileNamingConvention = 1;
    this.ftpFileFormat = AccountingFileFormat.Sie4;
    this.ftpFilePrefix = '';
    this.accountingClosedTo = null;
    this.cultureName = LanguageService.getCultureLang();
    this.currencyCode = Currencies.getDefaultCurrency();
    this.articleId = null;
    this.companyForm = CompanyForms.getDefaultCompanyForm();
    this.avyIntegration = {
      enabled: false,
      apiKey: null,
      sendPushNotificationsOnFaultNotificationUpdates: false,
      useMasterDataApi: false,
    };
    this.kivraIntegration = { enabled: false };
    this.dinBoxIntegration = { enabled: false, userName: null, password: null };
    this.bankAccounts = [];
    this.companyCostPlaces = [];
    this.companyCostBearers = [];
    this.companyCustomProperties = [];
    this.documents = [];
    this.invoicingInterval = 1;
    this.imdOffset = 2;
    this.dwellingInspectionTypeMainId = null;
    this.dwellingInspectionTypeOptionalId = null;
    this.sie4ExportConfiguration = new Sie4ExportConfiguration();
    this.gl07ExportConfiguration = new Gl07ExportConfiguration();
    this.raindanceExportConfiguration = new RaindanceExportConfiguration();
    this.vismaRorExportConfiguration = new VismaRorExportConfiguration();
    this.hogiaObConfiguration = new HogiaObExportConfiguration();
    this.emailAccountingExportConfiguration = new EmailAccountingConfiguration();
    this.accountingExportDateLimit = AccountingExportDateLimit.NoLimit;
    this.accountingTransferIntegrationType = AccountingExportMethodKind.None;
    this.nameplateType = CompanyNameplateType.Auto;
    this.amortizationPlanIntegration = { enabled: true, letterText: '', defaultArticleId: null };
  }

  amortizationPlanIntegration: { enabled: boolean; letterText: string; defaultArticleId: string | null };
  emailAccountingExportConfiguration: EmailAccountingConfiguration;
  sie4ExportConfiguration: Sie4ExportConfiguration;
  gl07ExportConfiguration: Gl07ExportConfiguration;
  raindanceExportConfiguration: RaindanceExportConfiguration;
  vismaRorExportConfiguration: VismaRorExportConfiguration;
  hogiaObConfiguration: HogiaObExportConfiguration;
  imdOffset: number;
  avyIntegration: {
    enabled: boolean;
    apiKey: string;
    sendPushNotificationsOnFaultNotificationUpdates: boolean;
    useMasterDataApi: boolean;
  };
  dinBoxIntegration: { enabled: boolean; userName: string; password: null };
  vismaControlEdgeIntegration: {
    enabled: boolean;
    tenantId: string;
    storageSpaceId: string;
    businessUnitName: string;
    o1: DimensionType;
    o2: DimensionType;
    o3: DimensionType;
  };
  accountPlanId: string;
  accountingExportDateLimit: AccountingExportDateLimit;
  address: Address;
  articleId: string;
  allowChangeNextInvoiceNumber: boolean;
  allowChangeCurrency: boolean;
  bankgiroNumber: string;
  closeAccountingAfterDays: number;
  companyForm: CompanyForm;
  cultureName: string;
  currencyCode: string;
  invoicingActive: boolean;
  invoicingBankAccountId: string;
  invoicingInterval: number;
  logo: Image;
  logoSrc: string;
  name: string;
  nextInvoiceNumber: number;
  number: number;
  onlineTerminationDwellingInspection: boolean;
  enableFaultNotificationDialog: boolean;
  enableFaultNotificationReporting: boolean;
  email: string;
  firstMonthInAccountingYear: number;
  invoiceFooter: string;
  issuerIdentity: string;
  eInvoiceAddress: string;
  eInvoiceSpecified: boolean;
  organizationNo: string;
  paymentReceiver: string;
  phone: string;
  vatNumber: string;
  ftpProtocol: string;
  ftpAddress: string;
  ftpPort: number;
  ftpUser: string;
  ftpPath: string;
  ftpPassword: string;
  ftpTimeOfDay: number;
  ftpFileNamingConventionSelection: { [id: number]: string } = {};
  ftpFileNamingConvention: number;
  ftpFileFormat: AccountingFileFormat;
  ftpFilePrefix: string;
  imdFtpEnabled: boolean;
  imdFtpUser: string;
  imdFtpChargeNegativeValues: boolean;
  imdFtpPassword: string;
  imdFtpServerAddress: string;
  imdFtpServerPort: string;
  verificationNumberSeriesInvoicing: string;
  verificationNumberSeriesGeneral: string;
  specialTermsDwelling: string;
  specialTermsParking: string;
  generalTermsDwelling: string;
  generalTermsParking: string;
  rentPaymentTermsDwelling: string;
  rentPaymentTermsParking: string;
  debtCollectionConfiguration: DebtCollectionConfiguration;
  debtCollectionSupplier: DebtCollectionSupplierKind;
  totalShare: number;
  bankAccounts: CompanyBankAccount[];
  companyCustomProperties: CompanyCustomProperty[];
  companyCostPlaces: CompanyCostPlace[];
  companyCostBearers: CompanyCostBearer[];
  accountingExportErrorEmailAddress: string;
  counterpart: string;
  hogiaOpenBusinessApiKey: string;
  documents: CompanyDocument[];
  useCounterpart: boolean;
  useCostPlace: boolean;
  useCostBearer: boolean;
  useProject: boolean;
  accountingTransferIntegrationType: AccountingExportMethodKind;
  dwellingInspectionTypeMainId: string;
  dwellingInspectionTypeOptionalId: string;
  nameplateType: CompanyNameplateType;
  useEarlyTermination: UseEarlyTerminationKind;
  invoiceFeeArticleId_Mail: string;
  invoiceFeeAmount_Mail: Money;
  invoiceFeeArticleId_Kivra: string;
  invoiceFeeAmount_Kivra: Money;
  invoiceFeeArticleId_B2CeInvoice: string;
  invoiceFeeAmount_B2CeInvoice: Money;
  invoiceFeeArticleId_B2BeInvoice: string;
  invoiceFeeAmount_B2BeInvoice: Money;
  kivraIntegration: { enabled: boolean };
  hasKivraLicense: boolean;

  get debtCollectionSupplierText(): string {
    return DebtCollectionSupplierKindToString(this.debtCollectionSupplier ?? DebtCollectionSupplierKind.None);
  }

  private _firstAccountingDateToExport: LocalDate;
  get firstAccountingDateToExport(): LocalDate {
    return LocalDate.fromDate(this._firstAccountingDateToExport);
  }
  set firstAccountingDateToExport(value: LocalDate) {
    this._firstAccountingDateToExport = LocalDate.fromDate(value);
  }
  private _accountingClosedTo: LocalDate;
  get accountingClosedTo(): LocalDate {
    return LocalDate.fromDate(this._accountingClosedTo);
  }
  set accountingClosedTo(value: LocalDate) {
    this._accountingClosedTo = LocalDate.fromDate(value);
  }
  private _economicalPlanSent: LocalDate;
  get economicalPlanSent(): LocalDate {
    return LocalDate.fromDate(this._economicalPlanSent);
  }
  set economicalPlanSent(value: LocalDate) {
    this._economicalPlanSent = LocalDate.fromDate(value);
  }
  private _economicalPlanApproved: LocalDate;
  get economicalPlanApproved(): LocalDate {
    return LocalDate.fromDate(this._economicalPlanApproved);
  }
  set economicalPlanApproved(value: LocalDate) {
    this._economicalPlanApproved = LocalDate.fromDate(value);
  }

  toJSON() {
    return {
      id: this.id,
      accountingExportErrorEmailAddress: this.accountingExportErrorEmailAddress,
      accountPlanId: this.accountPlanId,
      accountingExportDateLimit: this.accountingExportDateLimit,
      address: this.address,
      allowChangeNextInvoiceNumber: this.allowChangeNextInvoiceNumber,
      bankgiroNumber: this.bankgiroNumber,
      companyForm: this.companyForm,
      cultureName: this.cultureName,
      counterpart: this.counterpart,
      currencyCode: this.currencyCode,
      closeAccountingAfterDays: this.closeAccountingAfterDays,
      email: this.email,
      firstMonthInAccountingYear: this.firstMonthInAccountingYear,
      firstAccountingDateToExport: this._firstAccountingDateToExport,
      emailAccountingExportConfiguration: this.emailAccountingExportConfiguration,
      ftpAddress: this.ftpAddress,
      ftpFileFormat: this.ftpFileFormat,
      ftpPassword: this.ftpPassword,
      ftpPath: this.ftpPath,
      ftpPort: this.ftpPort,
      ftpProtocol: this.ftpProtocol,
      ftpTimeOfDay: this.ftpTimeOfDay,
      ftpUser: this.ftpUser,
      ftpFileNamingConvention: this.ftpFileNamingConvention,
      ftpFilePrefix: this.ftpFilePrefix,
      imdOffset: this.imdOffset,
      SIE4ExportConfiguration: this.sie4ExportConfiguration,
      gl07ExportConfiguration: this.gl07ExportConfiguration,
      raindanceExportConfiguration: this.raindanceExportConfiguration,
      vismaRorExportConfiguration: this.vismaRorExportConfiguration,
      hogiaObConfiguration: this.hogiaObConfiguration,
      invoiceFooter: this.invoiceFooter,
      invoicingActive: this.invoicingActive,
      invoicingBankAccountId: this.invoicingBankAccountId,
      invoicingInterval: this.invoicingInterval,
      logo: this.logo,
      number: this.number,
      name: this.name,
      nextInvoiceNumber: this.nextInvoiceNumber,
      onlineTerminationDwellingInspection: this.onlineTerminationDwellingInspection,
      organizationNo: this.organizationNo,
      paymentReceiver: this.paymentReceiver,
      phone: this.phone,
      vatNumber: this.vatNumber,
      verificationNumberSeriesInvoicing: this.verificationNumberSeriesInvoicing,
      verificationNumberSeriesGeneral: this.verificationNumberSeriesGeneral,
      eInvoiceAddress: this.eInvoiceAddress,
      eInvoiceSpecified: this.eInvoiceSpecified,
      specialTermsParking: this.specialTermsParking,
      specialTermsDwelling: this.specialTermsDwelling,
      generalTermsParking: this.generalTermsParking,
      generalTermsDwelling: this.generalTermsDwelling,
      rentPaymentTermsParking: this.rentPaymentTermsParking,
      rentPaymentTermsDwelling: this.rentPaymentTermsDwelling,
      debtCollectionConfiguration: this.debtCollectionConfiguration,
      articleId: this.articleId,
      imdFtpEnabled: this.imdFtpEnabled,
      imdFtpPassword: this.imdFtpPassword,
      imdFtpChargeNegativeValues: this.imdFtpChargeNegativeValues,
      totalShare: this.totalShare,
      economicalPlanSent: this.economicalPlanSent,
      economicalPlanApproved: this.economicalPlanApproved,
      kivraIntegration: this.kivraIntegration,
      hasKivraLicense: this.hasKivraLicense,
      dinBoxIntegration: this.dinBoxIntegration,
      companyCostBearers: this.companyCostBearers,
      companyCostPlaces: this.companyCostPlaces,
      companyCustomProperties: this.companyCustomProperties,
      hogiaOpenBusinessApiKey: this.hogiaOpenBusinessApiKey,
      documents: this.documents,
      enableFaultNotificationDialog: this.enableFaultNotificationDialog,
      enableFaultNotificationReporting: this.enableFaultNotificationReporting,
      vismaControlEdgeIntegration: this.vismaControlEdgeIntegration,
      accountingTransferIntegrationType: this.accountingTransferIntegrationType,
      dwellingInspectionTypeMainId: this.dwellingInspectionTypeMainId,
      dwellingInspectionTypeOptionalId: this.dwellingInspectionTypeOptionalId,
      issuerIdentity: this.issuerIdentity,
      nameplateType: this.nameplateType,
      useEarlyTermination: this.useEarlyTermination,
      invoiceFeeArticleId_Mail: this.invoiceFeeArticleId_Mail,
      invoiceFeeAmount_Mail: this.invoiceFeeAmount_Mail,
      invoiceFeeArticleId_Kivra: this.invoiceFeeArticleId_Kivra,
      invoiceFeeAmount_Kivra: this.invoiceFeeAmount_Kivra,
      invoiceFeeArticleId_B2CeInvoice: this.invoiceFeeArticleId_B2CeInvoice,
      invoiceFeeAmount_B2CeInvoice: this.invoiceFeeAmount_B2CeInvoice,
      invoiceFeeArticleId_B2BeInvoice: this.invoiceFeeArticleId_B2BeInvoice,
      invoiceFeeAmount_B2BeInvoice: this.invoiceFeeAmount_B2BeInvoice,
      amortizationPlanIntegration: this.amortizationPlanIntegration,
    };
  }

  toString() {
    return `${this.number ? this.number + ' ' : ''}${this.name}`;
  }
}

export class CompanyPostTarget extends ModelBase implements PostTargetModel {
  constructor(dataContext?: CompanyPostTarget) {
    super();
    if (dataContext) {
      this.id = dataContext.id || '';
      this.companyNumberName = dataContext.companyNumberName || '';
    }
  }

  companyNumberName: string;

  postTargetDescription() {
    return {
      key: 'POST.POST_TARGET_DESCRIPTION.COMPANY',
      params: { companyName: this.companyNumberName },
    };
  }

  toAutocompleteValue(viewType: 'list' | 'field', culture?: string): string {
    switch (viewType) {
      case 'field':
      case 'list':
        return this.companyNumberName;
    }
  }

  toString(culture?: string): string {
    return this.companyNumberName;
  }
}
